import React, { useEffect, useMemo, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import { useStaticOptions } from "../context/StaticOptionsContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { showErrorToast } from "../components/toasts/ToastService";

const Rama = () => {
    const { order, updateOrder } = useOrder();
    const navigate = useStepNavigate();
    const { staticOptions } = useStaticOptions();
    const { saveOrder } = useOrderSave();
    const [loading, setLoading] = useState(false);

    const ramaRows = staticOptions?.rama_types_and_colors || [];
    const isOpenNosedModel = Number(order?.model_options?.is_open_nosed) === 1;

    const filteredRows = useMemo(() => {
        return ramaRows.filter((row) => {
            if (isOpenNosedModel && Number(row?.rmeliramacolors_is_for_open_nosed) === 0) {
                return false;
            }
            return true;
        });
    }, [ramaRows, isOpenNosedModel]);

    const typeOptions = useMemo(() => {
        const map = new Map();

        filteredRows.forEach((row) => {
            const typeId = String(row?.rmeliramacolors_rama_type_id ?? "");
            const typeName = row?.rmeliramacolors_rama_type_national || "";

            if (!typeId || !typeName) return;
            if (!map.has(typeId)) {
                map.set(typeId, { value: typeId, label: typeName });
            }
        });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "hu"));
    }, [filteredRows]);

    const getColorOptionsForType = (typeId) => {
        if (!typeId) return [];

        const map = new Map();

        filteredRows
            .filter((row) => String(row?.rmeliramacolors_rama_type_id) === typeId)
            .forEach((row) => {
                const colorId = String(row?.rmeliramacolors_sole_color_id ?? "");
                const colorName = row?.rmeliramacolors_sole_color_national || "";
                if (!colorId || !colorName) return;

                if (!map.has(colorId)) {
                    map.set(colorId, { value: colorId, label: colorName });
                }
            });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "hu"));
    };

    const selectedFromOrder = useMemo(() => {
        const selectedId = order?.hmeliorder?.hmeliorder_rama_id;
        if (!selectedId || Number(selectedId) <= 1) return null;

        return (
            filteredRows.find(
                (r) => Number(r?.rmeliramacolors_szam) === Number(selectedId)
            ) || null
        );
    }, [filteredRows, order?.hmeliorder?.hmeliorder_rama_id]);

    const [selectedTypeId, setSelectedTypeId] = useState(
        order?.hmeliorder?.hmeliorder_rama_type_id && Number(order.hmeliorder.hmeliorder_rama_type_id) > 1
            ? String(order.hmeliorder.hmeliorder_rama_type_id)
            : ""
    );
    const [selectedColorId, setSelectedColorId] = useState(
        order?.hmeliorder?.hmeliorder_rama_sole_color_id && Number(order.hmeliorder.hmeliorder_rama_sole_color_id) > 1
            ? String(order.hmeliorder.hmeliorder_rama_sole_color_id)
            : ""
    );

    useEffect(() => {
        if (!selectedFromOrder) {
            return;
        }

        setSelectedTypeId(String(selectedFromOrder.rmeliramacolors_rama_type_id));
        setSelectedColorId(String(selectedFromOrder.rmeliramacolors_sole_color_id));
    }, [selectedFromOrder]);

    const colorOptionsForSelectedType = useMemo(
        () => getColorOptionsForType(selectedTypeId),
        [filteredRows, selectedTypeId]
    );

    const findRamaRow = (typeId, colorId) => {
        return (
            filteredRows.find(
                (r) =>
                    String(r?.rmeliramacolors_rama_type_id) === String(typeId) &&
                    String(r?.rmeliramacolors_sole_color_id) === String(colorId)
            ) || null
        );
    };

    const handleTypeSelect = (type) => {
        const nextTypeId = String(type.value);
        const colorsForType = getColorOptionsForType(nextTypeId);

        setSelectedTypeId(nextTypeId);
        setSelectedColorId("");

        // Nincs szín opció ehhez a típushoz -> a típus sora önmagában azonosítja a rámát
        const rowForType = colorsForType.length === 0
            ? filteredRows.find((r) => String(r?.rmeliramacolors_rama_type_id) === nextTypeId)
            : null;

        updateOrder((prev) => ({
            ...prev,
            hmeliorder: {
                ...prev.hmeliorder,
                hmeliorder_rama_type_id: type.value,
                hmeliorder_rama_type_national: type.label,
                hmeliorder_rama_sole_color_id: 1,
                hmeliorder_rama_sole_color_national: "",
                hmeliorder_rama_id: rowForType?.rmeliramacolors_szam != null ? Number(rowForType.rmeliramacolors_szam) : 1,
            },
        }));
    };

    const handleColorSelect = (color) => {
        const nextColorId = String(color.value);
        const matchedRow = findRamaRow(selectedTypeId, nextColorId);
        const matchedSzam = matchedRow?.rmeliramacolors_szam != null ? Number(matchedRow.rmeliramacolors_szam) : 1;

        setSelectedColorId(nextColorId);

        updateOrder((prev) => ({
            ...prev,
            hmeliorder: {
                ...prev.hmeliorder,
                hmeliorder_rama_sole_color_id: color.value,
                hmeliorder_rama_sole_color_national: color.label,
                hmeliorder_rama_id: matchedSzam,
            },
        }));
    };

    const handleBack = () => {
        navigate("/Koztalp");
    };

    const handleNext = async () => {
        if (!canProceed) {
            showErrorToast(
                "Kérem, válasszon ráma típust" + (colorOptionsForSelectedType.length > 0 ? " és színt" : "") + "!"
            );
            return;
        }

        try {
            setLoading(true);
            const result = await saveOrder(order);

            if (result && result.success) {
                navigate("/BelesTipus");
            } else {
                showErrorToast((result && result.message) || "Hiba történt a rendelés mentésekor!");
            }
        } catch (error) {
            console.error("Ráma mentési hiba:", error);
            showErrorToast("Hiba történt a rendelés mentésekor!");
        } finally {
            setLoading(false);
        }
    };

    const canProceed =
        Boolean(selectedTypeId) && (colorOptionsForSelectedType.length === 0 || Boolean(selectedColorId));

    return (
        <TablerLayout title="Ráma kiválasztása">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-body">
                        <h5 className="text-lg font-semibold mb-3">Kérem válasszon ráma típust</h5>

                        {typeOptions.length === 0 ? (
                            <div className="alert alert-warning">
                                Nincs választható ráma típus ehhez a modellhez.
                            </div>
                        ) : (
                            <div className="row g-3 mb-4">
                                {typeOptions.map((type) => (
                                    <div className="col-12 col-sm-6 col-md-4" key={type.value}>
                                        <button
                                            type="button"
                                            className={
                                                "btn w-100 py-3 fw-semibold " +
                                                (selectedTypeId === type.value ? "btn-primary" : "btn-outline-primary text-primary border-primary bg-white")
                                            }
                                            style={{
                                                fontSize: 18,
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                minHeight: 60,
                                            }}
                                            onClick={() => handleTypeSelect(type)}
                                        >
                                            {type.label}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <h5 className="text-lg font-semibold mb-3">Kérem válasszon ráma színt</h5>

                        {!selectedTypeId && (
                            <div className="alert alert-info">
                                Először válasszon ráma típust a színek megjelenítéséhez.
                            </div>
                        )}

                        {selectedTypeId && (
                            <div className="row g-3">
                                {colorOptionsForSelectedType.map((color) => (
                                    <div className="col-12 col-sm-6 col-md-4" key={color.value}>
                                        <button
                                            type="button"
                                            className={
                                                "btn w-100 py-3 fw-semibold " +
                                                (selectedColorId === color.value ? "btn-primary" : "btn-outline-primary text-primary border-primary bg-white")
                                            }
                                            style={{
                                                fontSize: 18,
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                minHeight: 60,
                                            }}
                                            onClick={() => handleColorSelect(color)}
                                        >
                                            {color.label}
                                        </button>
                                    </div>
                                ))}

                                {colorOptionsForSelectedType.length === 0 && (
                                    <div className="col-12">
                                        <div className="alert alert-warning mb-0">
                                            Ehhez a ráma típushoz jelenleg nincs szín opció.
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton side="left" label="Vissza" onClick={handleBack} />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleNext}
                            disabled={!canProceed || loading}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default Rama;